/**
 * Die Karte „Witz des Tages“ im Zuhause (16.10.2026).
 *
 * Leise Karte nach dem Vorbild der anderen Zuhause-Karten: ohne Witz bleibt
 * sie weg (kein Platzhalter), mit Witz zeigt sie Text, Vorlese-Knopf, Daumen
 * hoch/runter und „Noch einer“.
 *
 * - Vorlesen: derselbe Weg wie beim Tagesblatt — die Karte meldet nur Text und
 *   Platz (`voice-v8:vorlese-knopf`), den Knopf liefert app.ts.
 * - Daumen: POST /api/witz/:id/wertung; ein zweiter Tipp auf denselben Daumen
 *   nimmt die Wertung zurück. Die Wertung färbt die nächsten Witze.
 * - Design steht in style.css (.zuhause-witz*), hier keine Inline-Styles.
 */
import { zeichen } from "./zeichen.js";

export interface WitzDaten {
  id: string;
  text: string;
  wertung: -1 | 0 | 1;
}

export interface WitzKarte {
  wurzel: HTMLElement;
  /** Heutigen Witz holen und zeigen; ohne Witz bleibt die Karte verborgen. */
  lade: () => Promise<void>;
}

async function holeJson<T>(url: string, body?: unknown): Promise<T> {
  const r = await fetch(url, {
    method: body === undefined ? "GET" : "POST",
    credentials: "same-origin",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`witz ${r.status}`);
  return (await r.json()) as T;
}

export function baueWitzKarte(): WitzKarte {
  const sec = document.createElement("section");
  sec.className = "zuhause-witz zuhause-karte";
  sec.dataset.leiste = "witz";
  sec.hidden = true;

  const kopf = document.createElement("div");
  kopf.className = "zuhause-blick-kopf";
  const h = document.createElement("h2");
  h.className = "zuhause-blick-titel";
  h.textContent = "Witz des Tages";
  const vorlesen = document.createElement("span");
  vorlesen.className = "zuhause-vorlesen";
  kopf.append(h, vorlesen);

  const satz = document.createElement("p");
  satz.className = "zuhause-witz-text";
  satz.setAttribute("aria-live", "polite");

  const meldung = document.createElement("p");
  meldung.className = "zuhause-witz-meldung";
  meldung.setAttribute("role", "status");
  meldung.hidden = true;

  const knoepfe = document.createElement("div");
  knoepfe.className = "zuhause-karten-knoepfe zuhause-witz-knoepfe";

  const hoch = document.createElement("button");
  hoch.type = "button";
  hoch.className = "zuhause-rund-knopf zuhause-witz-daumen";
  hoch.setAttribute("aria-label", "Witz war gut");
  hoch.title = "Gut";
  hoch.setAttribute("aria-pressed", "false");
  hoch.innerHTML = zeichen("daumen-hoch", 18);

  const runter = document.createElement("button");
  runter.type = "button";
  runter.className = "zuhause-rund-knopf zuhause-witz-daumen";
  runter.setAttribute("aria-label", "Witz war nicht gut");
  runter.title = "Nicht gut";
  runter.setAttribute("aria-pressed", "false");
  runter.innerHTML = zeichen("daumen-runter", 18);

  const nochEiner = document.createElement("button");
  nochEiner.type = "button";
  nochEiner.className = "zuhause-pille";
  nochEiner.textContent = "Noch einer";

  knoepfe.append(hoch, runter, nochEiner);
  sec.append(kopf, satz, knoepfe, meldung);

  let aktuell: WitzDaten | null = null;

  const sagMeldung = (text: string | null): void => {
    meldung.textContent = text ?? "";
    meldung.hidden = !text;
  };

  const zeigeWertung = (w: -1 | 0 | 1): void => {
    hoch.setAttribute("aria-pressed", w === 1 ? "true" : "false");
    runter.setAttribute("aria-pressed", w === -1 ? "true" : "false");
  };

  const zeige = (w: WitzDaten | null): void => {
    aktuell = w;
    if (!w) {
      sec.hidden = true;
      return;
    }
    satz.textContent = w.text;
    zeigeWertung(w.wertung);
    sagMeldung(null);
    sec.hidden = false;
    window.dispatchEvent(
      new CustomEvent("voice-v8:vorlese-knopf", {
        detail: { text: w.text, platz: vorlesen },
      }),
    );
  };

  const bewerte = async (wunsch: 1 | -1): Promise<void> => {
    const w = aktuell;
    if (!w) return;
    // Zweiter Tipp auf denselben Daumen nimmt die Wertung zurück.
    const neu: -1 | 0 | 1 = w.wertung === wunsch ? 0 : wunsch;
    const alt = w.wertung;
    w.wertung = neu;
    zeigeWertung(neu);
    sagMeldung(null);
    try {
      await holeJson(`/api/witz/${encodeURIComponent(w.id)}/wertung`, { wert: neu });
    } catch {
      // Nicht so tun, als wäre es gespeichert.
      w.wertung = alt;
      if (aktuell === w) zeigeWertung(alt);
      sagMeldung("Die Wertung konnte nicht gespeichert werden.");
    }
  };
  hoch.addEventListener("click", () => void bewerte(1));
  runter.addEventListener("click", () => void bewerte(-1));

  nochEiner.addEventListener("click", () => {
    if (nochEiner.disabled) return;
    nochEiner.disabled = true;
    nochEiner.setAttribute("aria-busy", "true");
    sagMeldung(null);
    void holeJson<{ witz?: WitzDaten | null }>("/api/witz/noch-einer", {})
      .then((d) => {
        if (d.witz) zeige(d.witz);
        else sagMeldung("Gerade fällt mir keiner ein.");
      })
      .catch(() => sagMeldung("Gerade fällt mir keiner ein. Versuch es gleich nochmal."))
      .finally(() => {
        nochEiner.disabled = false;
        nochEiner.removeAttribute("aria-busy");
      });
  });

  const lade = async (): Promise<void> => {
    try {
      const d = await holeJson<{ an?: boolean; witz?: WitzDaten | null }>("/api/witz/heute");
      // Läuft gerade ein „Noch einer“, nicht darunter wegziehen.
      if (nochEiner.disabled) return;
      zeige(d.an && d.witz ? d.witz : null);
    } catch {
      /* Ohne Witz bleibt die Karte, wie sie ist — kein Platzhalter. */
    }
  };

  return { wurzel: sec, lade };
}
